/**
 * Dashboard_N figures (#2139, epic #2138): every number the Month view card
 * shows, derived from one `/flows` month entry and `/tangible`. Every input
 * is the SIGNED sum `/flows` keeps (spending negative, income positive), so
 * the code adds where the page's words subtract. The words for each figure
 * are in docs/dashboard-n-figures.md and the equations in
 * docs/dashboard-equations.md; the names here follow the epic's vocabulary
 * table.
 */

import { horizonOf, HORIZON_MONTHLY } from './budgetsByCategory';

const cents = (m, field) => (m && m[field]) || 0;

// One-off activity (#2210, epic #2201 Rules 1-9): lines on Pending Income and
// Planned Expenses budgets, read from the copies on each line (#2211), leave
// the comparison figures for the One-off budgets row and still count in Net,
// no exclusions (Net at close) and the Raw bar. Planning ahead money (#2277,
// epic #2251) is set aside the month it is funded: the Planning ahead budget
// row takes it off the top, so its spending counts nowhere on the month.

/**
 * Planning ahead budget row (#2277, sign fixed #2280): this month's funding
 * into Planning ahead budgets plus income tagged straight to one, negated,
 * since the engine stores money that went into a budget as a positive
 * amount and this row takes it off the top. A release (negative funding)
 * therefore reads as a plus. Every figure below Income is computed after it.
 */
// `0 -` rather than a unary minus, so an empty month reads 0, never -0.
export const planningSetAsideCents = (m) => 0 - (cents(m, 'planningFundingCents') + cents(m, 'planningIncomeCents'));

/**
 * Income: Income lines on Checking ledgers (#2261), less repayments to a
 * Pending Income budget (Rule 6).
 */
export const incomeRowCents = (m) => cents(m, 'checkingIncomeCents') - cents(m, 'oneOffIncomeCents');

/**
 * Savings income (#2261): Income lines on Savings ledgers, money set aside
 * for the long term, so kept out of Income and Baseline net and counted in
 * Net, no exclusions on its own Raw row.
 */
export const savingsIncomeCents = (m) => cents(m, 'savingsIncomeCents');

/** Spending lines tagged to a Month to month budget: all spending less the untagged and one-off parts. */
export const spendingFromBudgetsCents = (m) =>
  cents(m, 'spendingCents') - cents(m, 'unbudgetedSpendingCents') - cents(m, 'oneOffSpendingCents');

/** Net outlier money: one-time out (negative) plus one-time in. */
export const outliersCents = (m) => cents(m, 'oneTimeInCents') + cents(m, 'oneTimeOutCents');

/**
 * One-off budgets: spending and repayments on Pending Income and Planned
 * Expenses budgets, netted. Planning ahead spending left this row with #2277:
 * it was paid from money the Planning ahead budget row already set aside.
 */
export const oneOffBudgetsCents = (m) =>
  cents(m, 'oneOffSpendingCents') - cents(m, 'planningSpendingCents') + cents(m, 'oneOffIncomeCents');

/**
 * Baseline net: Income, less the Planning ahead set-aside (#2277), plus both
 * spending rows; transfers, outliers and one-off budgets set aside. The
 * chart's Baseline bar.
 */
export const baselineNetCents = (m) =>
  incomeRowCents(m) + planningSetAsideCents(m) + spendingFromBudgetsCents(m) + cents(m, 'unbudgetedSpendingCents');

/**
 * Net, no exclusions ("Net at close" on a closed month): Baseline net with
 * transfers, outliers, one-off budgets and Savings income (#2261) counted,
 * so every line of the month. The chart's Raw bar.
 */
export const everythingCents = (m) =>
  baselineNetCents(m) + cents(m, 'transferNetCents') + outliersCents(m) + oneOffBudgetsCents(m) + savingsIncomeCents(m);

/**
 * Budgeted funds remaining (#2253, epic #2251): the month's funding into the
 * Month to month budgets plus every line tagged to them that month, added
 * up from the `/budget-months` rows. Signed, so an overspent budget counts
 * below zero. Null when the rows are not there, so the figures that need it
 * stay off the page instead of reading $0.00.
 * @param {Array|null|undefined} rows  the `/budget-months` budgets
 * @returns {number|null}
 */
export const budgetedRemainingCents = (rows) => {
  if (!rows) return null;
  return rows
    .filter((b) => (b.type ?? 0) === 0 && horizonOf(b) === HORIZON_MONTHLY)
    // Every field that moves the budget's balance within the month.
    .reduce((sum, b) => sum + cents(b, 'fundingCents') + cents(b, 'spendingCents') + cents(b, 'incomeCents') + cents(b, 'transferCents') + cents(b, 'oneTimeCents'), 0);
};

/**
 * Net: month end projection, current month only (#2253): Baseline net less
 * Budgeted funds remaining. Spending from a Month to month budget moves
 * Spending from budgets and the remaining amount by the same figure, so the
 * projection holds still.
 */
export const netIfSpentCents = (m, remainingCents) => baselineNetCents(m) - (remainingCents || 0);

/**
 * Checking, the top bar's first figure (#2206): the active Checking ledgers'
 * balances from `/tangible` `byAccount` (Checking and Credit Card rows carry
 * `type` and `archived`). Archived ledgers stay out (owner, #2139 settle
 * point 3).
 * @param {Array<{type: number, archived?: boolean, balanceCents: number}>} byAccount
 */
export const checkingCents = (byAccount) => (byAccount || [])
  .filter((l) => l.type === 0 && !l.archived)
  .reduce((sum, l) => sum + (l.balanceCents || 0), 0);

/**
 * The month switcher's list (#2139): the current month always, plus every
 * month with lines, newest first. Each carries `closed` so the dropdown can
 * say "in progress" or "closed".
 * @param {Array<{yearMonth: string, lineCount: number}>} months  oldest first
 * @param {string} currentYearMonth
 */
export const switcherMonths = (months, currentYearMonth) => (months || [])
  .filter((m) => m.yearMonth === currentYearMonth || (m.lineCount || 0) > 0)
  .map((m) => ({ yearMonth: m.yearMonth, closed: m.yearMonth !== currentYearMonth }))
  .reverse();
